import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
    listProcessMaterials,
    listProcessRecords,
    getProcessStats,
    getPrismHealth,
    getProcessOptions,
    PrismArchive,
    PrismMaterialRow,
    PrismProcSummary,
    ProcDateWindow,
    ProcListParams,
    ProcLocalParams,
    ProcLocalRowExtra,
    ProcLocalSummaryExtra,
    ProcOptions,
    ProcOrigin,
} from '../../api/assets';
import { ApiError } from '../../api/request';
import ImageLightbox from '../../components/ImageLightbox';
import Pagination from '../../components/Pagination';
import { viewUrl } from '../../utils/imgUrl';
import { useSyncQueryToUrl } from '../../utils/urlState';
import { AdoptBadge, BarList, SortTimeHeader, DataSourceBar, DateRangeInput, EffectBadge, NoticeBar, ProcessThumb, SOURCE_KIND_LABEL, SatisfactionBadge, SegGroup, SourceKindBadge, StatCard, SuccessRateBars, TIER3, defectText, deviceColorMap, isSentinelMaterialID, materialDisplayName, tier3Of } from './parts';
import PrismSyncBar from './PrismSyncBar';
import ProcLocalSection, { FilterGrid, FilterGroup, SegRow } from './ProcLocalFilters';
import ProcLocalStats from './ProcLocalStats';
import { PROC_HISTORY_ONLY_KEYS, PROC_LOCAL_KEYS, PROC_LOCAL_LABEL, procLocalFromQuery } from './procLocalText';
import { usePrismReadLocal } from '../../utils/prismReadLocal';
import { useProcessTypeText } from '../../utils/dictOptions';
import { qualityTierCounts } from './quality';
import SceneCard, { SceneDist } from './SceneCard';
import SearchSelect from '../../components/SearchSelect';
import HelpTip from '../../components/HelpTip';

// 数据资产·加工数据中心 — UI 对齐 studio-admin-console「材料加工」页.
// 2026-09-02 起**不做数据同步**: 后端实时代理 prism-service-api 的
// /dashboard/processing/* (summary / materials / record/{id} / label).
//
// 明细有两个维度 (2026-09-20 新增前者, 并设为默认):
//   record   一行 = 一次加工上报 (存档行), 带上报用户, 「参数采纳」「加工质量」是这一次自己的判定;
//   material 一行 = 一个材料 (跨用户聚合), 「加工次数」是所有用户的合计, 判定取最近一次.
// 加了 record 维度是因为聚合视图看不出每次上报各自的效果 —— 材料 985 当天 690 次是 85 个
// 用户摊出来的, 聚合行只显示其中最近那一次的判定.
//
// 2026-10-20 prism 同步落库: 后端 readLocal=true 后改读本库, 并入 XCS 2.7 历史加工数据 ——
// 页面多出「数据来源」分段 (全部 / Studio 实时 / XCS 2.7 历史)、一组新筛选与新统计卡 (ProcLocal*);
// 只有 Studio 实时才有的卡 (采纳 / 效果 / 官方材料·官方参数) 在「全部」下标「仅 Studio 实时」, 选历史时隐藏.
// readLocal=false 时以上全部不出现, 页面与改造前一致.

const PAGE_SIZE = 20;
const HAS_RESULT_IMAGE_OPTS = [
    { value: 'true', label: '有结果图' },
    { value: 'false', label: '无结果图' },
];

/** 维度: 一行一次加工上报 / 一行一个材料. */
type Grain = 'record' | 'material';

/** 生效中的一项筛选 (底部 chip): advanced = 属于「更多筛选」层, 用来给折叠按钮计数. */
interface ActiveChip { key: string; label: string; clear: () => void; advanced: boolean }

/** 属于「更多筛选」层的 URL 参数 (首屏有任一 → 默认展开). */
const ADVANCED_QUERY_KEYS = ['labeled', 'defect', 'indicator', 'scene', 'has_result_image'];

/**
 * MIN_OFFICIAL_N 「官方材料 · 官方参数」这一档要多少条已判定加工才算说得上话.
 * 取 30 是统计上最松的那道线; 低于它卡上打「样本不足」, 因为这一档的分母天生小 ——
 * 官方材料那条路径上大多数加工判不出"有没有采纳推荐参数"
 * (见 doc/数据资产/加工成功率口径分析-20260909.md).
 */
const MIN_OFFICIAL_N = 30;

function fmtDay(s?: string): string {
    if (!s) return '—';
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    const p = (x: number) => String(x).padStart(2, '0');
    return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())}`;
}

/** 加工记录一行一次上报, 同一天里也要分得出先后, 故显示到分钟. */
function fmtMinute(s?: string): string {
    if (!s) return '—';
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    const p = (x: number) => String(x).padStart(2, '0');
    return `${fmtDay(s)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** prism 的 `scene` 是 snake_case + `code`, 归一到 SceneCard 的形状。 */
function toSceneDist(s?: PrismProcSummary['scene']): SceneDist | undefined {
    if (!s) return undefined;
    return {
        labeled: s.labeled, evaluable: s.evaluable, unevaluable: s.unevaluable,
        tags: s.tags.map((t) => ({ key: t.code, count: t.count })),
        unableReasons: s.unable_reasons.map((r) => ({ key: r.code, count: r.count })),
    };
}

function ProcessList() {
    const history = useHistory();
    const initQ = new URLSearchParams(useLocation().search);
    const [tab, setTab] = useState(initQ.get('tab') === 'table' ? 'table' : 'stats');
    const [grain, setGrain] = useState<Grain>(initQ.get('grain') === 'material' ? 'material' : 'record');
    const [device, setDevice] = useState(initQ.get('device') || '');
    const [quality, setQuality] = useState(initQ.get('quality') || '');
    const [verdict, setVerdict] = useState(initQ.get('verdict') || '');
    const [recognizedOnly, setRecognizedOnly] = useState(initQ.get('recognized_only') || '');
    const [source, setSource] = useState(initQ.get('source') || '');
    const [category, setCategory] = useState(initQ.get('category') || '');
    const [uid, setUid] = useState(initQ.get('uid') || '');
    const [uidDraft, setUidDraft] = useState(initQ.get('uid') || '');
    const [categories, setCategories] = useState<string[]>([]);
    const [defect, setDefect] = useState(initQ.get('defect') || '');
    const [indicator, setIndicator] = useState(initQ.get('indicator') || '');
    const [archiveId, setArchiveId] = useState(initQ.get('archive_id') || '');
    const [archiveIdDraft, setArchiveIdDraft] = useState(initQ.get('archive_id') || '');
    const [materialQ, setMaterialQ] = useState(initQ.get('material_q') || '');
    const [materialQDraft, setMaterialQDraft] = useState(initQ.get('material_q') || '');
    const [labeled, setLabeled] = useState(initQ.get('labeled') || '');
    const [hasResultImage, setHasResultImage] = useState(initQ.get('has_result_image') || '');
    const [scene, setScene] = useState(initQ.get('scene') || '');
    const [satisfied, setSatisfied] = useState(initQ.get('satisfied') || '');
    const [dateFrom, setDateFrom] = useState(initQ.get('date_from') || '');
    const [dateTo, setDateTo] = useState(initQ.get('date_to') || '');
    const [page, setPage] = useState(Number(initQ.get('page')) || 1);
    // 明细按加工时间排序 (仅读本库 · 每次加工上报维度): 默认新→旧.
    const [order, setOrder] = useState<'asc' | 'desc'>(initQ.get('order') === 'asc' ? 'asc' : 'desc');
    // 读本库后才有的筛选 (数据来源 + 新维度), 一个对象管, 键表见 procLocalText.PROC_LOCAL_KEYS.
    const readLocal = usePrismReadLocal();
    // 明细 (列表 / 按材料) 实际生效的日期窗口: 未选日期时后端默认最近 30 天; 统计不受此限, 看全部.
    const [listWindow, setListWindow] = useState<ProcDateWindow | null>(null);
    const [lf, setLf] = useState<ProcLocalParams>(() => procLocalFromQuery(initQ));
    const origin: ProcOrigin = readLocal ? (lf.origin || '') : '';
    const setLocal = (k: keyof ProcLocalParams, v: string) => {
        setLf((prev) => {
            const next = { ...prev, [k]: v || undefined };
            // 只看 Studio 实时时, 历史专属筛选没意义, 一并清掉 (否则筛出来恒为空).
            if (k === 'origin' && v === 'studio') PROC_HISTORY_ONLY_KEYS.forEach((h) => { delete next[h]; });
            return next;
        });
        setPage(1);
    };
    const [options, setOptions] = useState<ProcOptions | null>(null);
    const ptText = useProcessTypeText();

    const [list, setList] = useState<PrismMaterialRow[]>([]);
    const [records, setRecords] = useState<(PrismArchive & ProcLocalRowExtra)[]>([]);
    const [total, setTotal] = useState(0);
    const [sentinel, setSentinel] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [sum, setSum] = useState<(PrismProcSummary & ProcLocalSummaryExtra) | null>(null);
    const [health, setHealth] = useState<{ enabled: boolean; ok?: boolean; error?: string } | null>(null);
    const [zoomSrc, setZoomSrc] = useState<string | null>(null);

    useSyncQueryToUrl('/assets/process-records', {
        tab: tab === 'stats' ? '' : tab, grain: grain === 'record' ? '' : grain,
        device, quality, verdict, recognized_only: recognizedOnly,
        source, category, uid, defect, indicator, archive_id: archiveId, material_q: materialQ, labeled,
        has_result_image: hasResultImage, scene, satisfied, date_from: dateFrom, date_to: dateTo, page,
        ...(readLocal ? lf : {}),
        order: readLocal && order === 'asc' ? 'asc' : '',
    });

    // 读本库时随请求带上的新筛选 (未切换时一个都不发, 老后端也不认识).
    const localParams = readLocal ? lf : {};
    const localKey = JSON.stringify(localParams);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        const params: ProcListParams = {
            device, quality, verdict,
            source: source || undefined, category: category || undefined, uid: uid || undefined,
            recognized_only: recognizedOnly || undefined,
            defect: defect || undefined,
            indicator: indicator || undefined,
            archive_id: archiveId || undefined,
            material_q: materialQ || undefined,
            labeled: labeled || undefined,
            has_result_image: hasResultImage || undefined,
            scene: scene || undefined,
            satisfied: satisfied || undefined,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            order: readLocal && grain === 'record' && order === 'asc' ? 'asc' : undefined,
            page, pageSize: PAGE_SIZE,
        };
        const withLocal = { ...params, ...(JSON.parse(localKey) as ProcLocalParams) };
        try {
            if (grain === 'record') {
                // 加工记录维度不剔除无 material_id 的存档 —— 它们本来就是一次真实上报,
                // 只是归不到材料上; 聚合视图里那条"另有 N 条"提示在这里不适用.
                const res = await listProcessRecords(withLocal);
                setRecords(res.data || []);
                setTotal(res.total);
                setSentinel(0);
                setListWindow(res.date_window || null);
                if (res.categories) setCategories(res.categories);
            } else {
                const res = await listProcessMaterials(withLocal);
                setList(res.data || []);
                setTotal(res.total);
                setSentinel(res.sentinel_archives || 0);
                setListWindow(res.date_window || null);
                if (res.categories) setCategories(res.categories);
            }
        } catch (e) {
            setError(e instanceof ApiError ? e.message : String(e));
            setList([]);
            setRecords([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [grain, device, quality, verdict, recognizedOnly, source, category, uid, defect, indicator,
        archiveId, materialQ, labeled, hasResultImage, scene, satisfied, dateFrom, dateTo, page, localKey, readLocal, order]);

    const loadStats = useCallback(async () => {
        try {
            setSum(await getProcessStats({
                device, date_from: dateFrom || undefined, date_to: dateTo || undefined,
                ...(JSON.parse(localKey) as ProcLocalParams),
            }));
        } catch { /* 不阻塞明细 */ }
    }, [device, dateFrom, dateTo, localKey]);
    // 新筛选的下拉选项 (带条数), 跟着数据来源变.
    useEffect(() => {
        if (!readLocal) return;
        getProcessOptions({ origin }).then(setOptions).catch(() => setOptions(null));
    }, [readLocal, origin]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadStats(); }, [loadStats]);
    // prism 同步跑完 (手动触发) → 列表与统计一起刷新.
    const onPrismSynced = useCallback(() => { load(); loadStats(); }, [load, loadStats]);
    useEffect(() => { getPrismHealth().then(setHealth).catch(() => undefined); }, []);

    const resetFilters = () => {
        setDevice(''); setQuality(''); setVerdict(''); setRecognizedOnly('');
        setSource(''); setCategory(''); setUid(''); setUidDraft('');
        setDefect(''); setIndicator(''); setMaterialQ(''); setMaterialQDraft('');
        setArchiveId(''); setArchiveIdDraft('');
        setLabeled(''); setHasResultImage(''); setScene(''); setSatisfied('');
        setDateFrom(''); setDateTo(''); setPage(1);
        // 数据来源是分段开关不是筛选, 清空时保留.
        setLf((prev) => ({ origin: prev.origin }));
    };
    const setDates = (from: string, to: string) => { setDateFrom(from); setDateTo(to); setPage(1); };
    // 参数采纳卡下钻: 卡上数的是记录 → 落到记录维度.
    const drillVerdict = (v: string) => { setVerdict(v); setGrain('record'); setPage(1); setTab('table'); };
    // 统计卡下钻: 合并筛选并切到明细.
    const drillLocal = (p: ProcLocalParams) => {
        setLf((prev) => ({ ...prev, ...p }));
        setPage(1);
        setTab('table');
    };
    // 只有 Studio 实时才有的卡: 选历史时隐藏, 「全部」时打标签说明分母只含实时数据.
    const studioOnly = readLocal && origin === 'history';
    const studioTag = readLocal && origin === '' ? (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-500 font-normal">仅 Studio 实时</span>
    ) : null;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    // 各设备成功率的排序方向 (默认低→高: 先看最差的那几台).
    const [rateDesc, setRateDesc] = useState(false);
    const funnel = sum?.funnel;
    const effect = sum?.effect;
    const adoption = sum?.adoption;
    // 两套口径八个键归并成三档 (parts.tsx qualityTierCounts); 「无法判断」不进分母.
    const tiers = qualityTierCounts(effect);
    const tierBase = tiers.base;
    const heroRate = effect && tierBase > 0 ? Math.round(((tiers.perfect + tiers.minor) / tierBase) * 100) : null;
    const topInd = effect?.top_indicators || [];
    // 原型首屏卡的口径: 官方材料 + 官方参数 (定义见 api/assets.ts effect.official 注释).
    // 现在能出数, 但样本很小 (dev 实测个位数), 所以 < MIN_OFFICIAL_N 时卡上标「样本不足」——
    // 百分比照给, 但不允许它看起来像个可下结论的指标.
    const off = effect?.official;
    const offTiers = qualityTierCounts(off);
    const offBase = offTiers.base;
    const offRate = off && offBase > 0 ? Math.round(((offTiers.perfect + offTiers.minor) / offBase) * 100) : null;
    const offThin = offBase > 0 && offBase < MIN_OFFICIAL_N;
    // 配色按加工量降序的固定顺序分配, 不受排序开关与筛选影响 —— 同一台设备恒为同色.
    const devColor = deviceColorMap(
        [...(sum?.by_device || [])]
            .sort((a, b) => qualityTierCounts(b).base - qualityTierCounts(a).base)
            .map((d) => d.device || '未知'),
    );
    const deviceRates = (sum?.by_device || [])
        .map((d) => {
            const t = qualityTierCounts(d);
            return { label: d.device || '未知', rate: t.base > 0 ? Math.round(((t.perfect + t.minor) / t.base) * 100) : -1, n: t.base };
        })
        .filter((d) => d.n > 0 && d.rate >= 0);
    const byCategory = (sum?.by_category || []).filter((c) => c.category !== 'Unknown');
    const unknownCat = (sum?.by_category || []).find((c) => c.category === 'Unknown');
    const adoptionOk = adoption ? adoption.unresolved <= adoption.compared : false;
    const adoptTotal = adoption ? adoption.adopted + adoption.adjusted + adoption.no_recommendation : 0;
    // 按材料聚合的几张图 (来源 / 分类 / Top 材料) 只能覆盖**带材料标识**的加工;
    // 源库里 material_id 为空或 '0' 的存档归不到材料上, 必须把这个缺口写在卡上,
    // 否则那几张图看起来像是覆盖了全部 process_events.
    const eventsIdentified = funnel?.process_events_identified ?? funnel?.process_events ?? 0;
    const sentinelEvents = (funnel?.process_events ?? 0) - eventsIdentified;
    const sentinelNote = sentinelEvents > 0 ? (
        <div className="text-[11px] text-amber-600">
            另有 {sentinelEvents.toLocaleString()} 次加工没带材料标识（源库 material_id 为空或 0），
            归不到任何材料上，不在这张图里 —— 本图覆盖 {eventsIdentified.toLocaleString()} / {(funnel?.process_events ?? 0).toLocaleString()} 次加工。
        </div>
    ) : undefined;

    // 生效中的筛选 (只列一次: 底部 chip、表头「已筛」提示、折叠按钮计数三处共用).
    // 读本库新增的键走 PROC_LOCAL_KEYS / PROC_LOCAL_LABEL, 同名的 (功率上下限 / 速度上下限) 合成一个 chip.
    const reset = (fn: () => void) => () => { fn(); setPage(1); };
    const baseChips: (ActiveChip | false | '')[] = [
        archiveId && { key: 'archive_id', label: '归档号', advanced: false, clear: reset(() => { setArchiveId(''); setArchiveIdDraft(''); }) },
        materialQ && { key: 'material_q', label: '材料', advanced: false, clear: reset(() => { setMaterialQ(''); setMaterialQDraft(''); }) },
        uid && { key: 'uid', label: '用户', advanced: false, clear: reset(() => { setUid(''); setUidDraft(''); }) },
        (dateFrom || dateTo) && { key: 'date', label: '日期', advanced: false, clear: reset(() => { setDateFrom(''); setDateTo(''); }) },
        device && { key: 'device', label: '设备', advanced: false, clear: reset(() => setDevice('')) },
        category && { key: 'category', label: '分类', advanced: false, clear: reset(() => setCategory('')) },
        source && { key: 'source', label: '来源', advanced: false, clear: reset(() => setSource('')) },
        quality && { key: 'quality', label: '加工质量', advanced: false, clear: reset(() => setQuality('')) },
        verdict && { key: 'verdict', label: '参数采纳', advanced: false, clear: reset(() => setVerdict('')) },
        satisfied && { key: 'satisfied', label: '满意度', advanced: false, clear: reset(() => setSatisfied('')) },
        labeled && { key: 'labeled', label: '打标状态', advanced: true, clear: reset(() => setLabeled('')) },
        defect && { key: 'defect', label: '缺陷', advanced: true, clear: reset(() => setDefect('')) },
        indicator && { key: 'indicator', label: '指标', advanced: true, clear: reset(() => setIndicator('')) },
        scene && { key: 'scene', label: '加工场景', advanced: true, clear: reset(() => setScene('')) },
        hasResultImage && { key: 'has_result_image', label: '结果图', advanced: true, clear: reset(() => setHasResultImage('')) },
    ];
    const localChips: ActiveChip[] = [];
    if (readLocal) {
        PROC_LOCAL_KEYS.filter((k) => k !== 'origin' && lf[k]).forEach((k) => {
            const label = PROC_LOCAL_LABEL[k] || k;
            if (localChips.some((c) => c.label === label)) return;
            const same = PROC_LOCAL_KEYS.filter((x) => PROC_LOCAL_LABEL[x] === label);
            localChips.push({
                key: k, label, advanced: k !== 'device_module',
                clear: () => {
                    setLf((prev) => {
                        const next = { ...prev };
                        same.forEach((x) => { delete next[x]; });
                        return next;
                    });
                    setPage(1);
                },
            });
        });
    }
    const chips = [...baseChips.filter((c): c is ActiveChip => !!c), ...localChips];
    const activeFilters = chips.map((c) => c.label);
    const advancedCount = chips.filter((c) => c.advanced).length;
    // 「更多筛选」默认收起; 进页面时 URL 里已带高级筛选 → 展开, 免得筛了看不见.
    const [moreOpen, setMoreOpen] = useState(() => ADVANCED_QUERY_KEYS.some((k) => initQ.get(k))
        || PROC_LOCAL_KEYS.some((k) => k !== 'origin' && k !== 'device_module' && initQ.get(k)));
    // 打标状态与打标来源合成一组: 已打标 / 待打标 写 labeled, AI / 人工 写 label_source (两个参数与后端口径不变).
    const labelSrc = readLocal ? lf.label_source : undefined;
    const labelSeg = labelSrc === 'ai' || labelSrc === 'manual' ? labelSrc
        : labeled === 'true' ? 'labeled'
            : labeled === 'false' || labelSrc === 'none' ? 'unlabeled' : '';
    const onLabelSeg = (x: string) => {
        setLabeled(x === 'labeled' ? 'true' : x === 'unlabeled' ? 'false' : '');
        setLf((prev) => ({ ...prev, label_source: x === 'ai' || x === 'manual' ? x : undefined }));
        setPage(1);
    };
    const toggleOrder = () => { setOrder((o) => (o === 'asc' ? 'desc' : 'asc')); setPage(1); };
    const scopeText = [
        recognizedOnly === 'true' ? '只看识别链路的加工' : '全部加工记录（与统计同口径）',
        activeFilters.length > 0 ? `已筛：${activeFilters.join(' · ')}` : '未筛选',
    ].join(' · ');

    return (
        <div className="space-y-5">
            {/* 页头 */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="text-[10px] font-extrabold tracking-[4px] uppercase text-slate-400 mb-1">Prism · Processing</div>
                    <div className="text-xl font-extrabold text-slate-900 tracking-tight">材料加工</div>
                    <div className="text-xs text-slate-500 mt-1.5">
                        被拿去加工的材料 —— 加工了多少、效果怎么样、参数怎么调。
                        {tab === 'stats' ? (
                            <span className="text-slate-400"> 当前口径：<b className="font-semibold text-slate-500">全部加工归档</b> —— 官方材料、用户自定义、AI 识别保存的都算在内。</span>
                        ) : (
                            <span className="text-slate-400"> 这张表与「统计」tab <b className="font-semibold text-slate-500">同口径</b>（全部加工归档）。「口径」开关可切成只看走过识别链路的那批。</span>
                        )}
                    </div>
                </div>
                <div className="flex items-start gap-4 shrink-0">
                    {/* 读路径切到本库前仍直连 prism (DataSourceBar), 同步状态条同时显示回填进度 */}
                    {!readLocal && <DataSourceBar health={health} />}
                    <PrismSyncBar onSynced={onPrismSynced} />
                </div>
            </div>

            {/* tabs (统计默认; 统计 tab 无筛选栏) */}
            <div className="flex items-center gap-1">
                {[['stats', '统计'], ['table', '加工明细']].map(([key, label]) => (
                    <button key={key} type="button"
                        className={`px-[15px] py-1.5 rounded-[9px] text-[13px] font-semibold ${tab === key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                        onClick={() => setTab(key)}>
                        {label}
                    </button>
                ))}
                {readLocal && (
                    <div className="ml-auto">
                        <SegGroup label="数据来源" value={origin} onChange={(v) => setLocal('origin', v)}
                            options={[{ value: '', label: '全部' }, { value: 'studio', label: 'Studio 实时' }, { value: 'history', label: 'XCS 2.7 历史' }]} />
                    </div>
                )}
            </div>

            {tab === 'stats' ? (
                <div className="space-y-4">
                    {/* 统计的日期范围 (与明细共用同一份日期筛选; 不选 = 全部数据) */}
                    <div className="card px-4 py-2.5 flex flex-wrap items-center gap-3">
                        <span className="text-xs text-slate-400">统计日期</span>
                        <DateRangeInput from={dateFrom} to={dateTo} onChange={setDates} presets className="w-[620px] max-w-full" />
                        <span className="ml-auto text-[11px] text-slate-400">
                            {dateFrom || dateTo ? `${dateFrom || '最早'} ~ ${dateTo || '最新'}（UTC，含首尾两天）` : '未选日期：统计全部数据'}
                        </span>
                    </div>
                    <NoticeBar>
                        这里统计的是 <b>全部加工归档</b>被拿去加工的情况：加工了多少、效果怎么样、参数怎么调。
                        材料名取<b>本平台官方材料主数据</b>；用户自定义材料没有对应主数据，名称留空、只按 ID 归组。
                    </NoticeBar>

                    {/* 官方材料 · 官方参数 加工成功率 (原型首屏卡) —— 全量达标率退为辅助信息 */}
                    {!studioOnly && (
                    <div className="card p-5 grid grid-cols-[1fr_1.2fr] gap-6 items-center">
                        <div>
                            <div className="flex items-baseline gap-2">
                                <div className="text-sm font-semibold text-slate-700">官方材料 · 官方参数 加工成功率</div>
                                {studioTag}
                                <div className="text-[11px] text-slate-400">识别命中官方耗材 + 原样采用推荐参数</div>
                                {offThin && (
                                    <div className="text-[11px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600">样本不足</div>
                                )}
                            </div>
                            <div className="text-[52px] leading-none font-extrabold text-indigo-600 tabular-nums mt-2">
                                {offRate != null ? `${offRate}%` : '暂无'}
                            </div>
                            {/* 文案按线上实测重写 (2026-09-22): 线上 adopted 2,366 条的归档材料**全部**是
                                用户素材库的雪花 id, 判成"官方材料"全靠识别候选带的耗材编码 —— 所以这张卡
                                衡量的是「识别命中官方耗材 + 原样采用**识别给的**推荐参数」, 不是"用户在平台
                                选了官方材料 + 用了参数库的推荐值"。原来那句"越高说明官方推荐参数越可信"
                                会把它读成参数库的可信度, 已删。 */}
                            <div className="text-[11px] text-slate-400 mt-2 leading-relaxed">
                                口径:<b>材料</b>能关联到平台官方耗材(归档材料对上主数据，<b>或</b>识别候选带的耗材编码解析到平台材料)，
                                且这次加工<b>原样采用了识别给出的推荐参数</b>；分子是效果「完美 + 轻微瑕疵」，分母只含已判定的加工，
                                图像不可判读的已排除。
                                <br />
                                它衡量的是<b>识别链路</b>那套推荐参数的效果，<b>不代表</b>平台参数库推荐值的可信度 —— 后者还没接进采纳判定
                                （直选官方材料的加工判不出采纳与否，见口径分析文档）。
                            </div>
                        </div>
                        <div>
                            {off && offBase > 0 ? (
                                <>
                                    <div className="text-right font-mono text-[11px] text-slate-500">
                                        （完美 {offTiers.perfect.toLocaleString()} + 轻微瑕疵 {offTiers.minor.toLocaleString()}） / 总计 {offBase.toLocaleString()} = {offRate}%
                                    </div>
                                    <div className="flex h-3.5 rounded-full overflow-hidden mt-2">
                                        <div style={{ width: `${(offTiers.perfect / offBase) * 100}%`, background: '#10b981' }} title={`完美 ${offTiers.perfect}`} />
                                        <div style={{ width: `${(offTiers.minor / offBase) * 100}%`, background: '#fbbf24' }} title={`轻微瑕疵 ${offTiers.minor}`} />
                                        <div style={{ width: `${(offTiers.fail / offBase) * 100}%`, background: '#fb7185' }} title={`不达标 ${offTiers.fail}`} />
                                    </div>
                                    <div className="flex gap-4 mt-2 text-[11px] text-slate-500 justify-end">
                                        <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: '#10b981' }} />完美 {offTiers.perfect.toLocaleString()}</span>
                                        <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: '#fbbf24' }} />轻微瑕疵 {offTiers.minor.toLocaleString()}</span>
                                        <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: '#fb7185' }} />不达标 {offTiers.fail.toLocaleString()}</span>
                                    </div>
                                    <div className="text-right text-[11px] text-slate-400 mt-2 leading-relaxed">
                                        这一档共 {off.scanned.toLocaleString()} 次加工
                                        {off.unlabeled > 0 ? `，另有 ${off.unlabeled.toLocaleString()} 次还没打标、不在分母里` : ''}。
                                        {offThin && ' 样本太少，只当个体案例看，别拿它下结论。'}
                                        <br />
                                        对照:全部加工归档的达标率 <b>{heroRate != null ? `${heroRate}%` : '—'}</b>（已判定 {tierBase.toLocaleString()} 条）。
                                        {heroRate != null && offRate != null && offRate < heroRate && (
                                            <>
                                                {' '}这一档<b>低于</b>全量，别读成"官方推荐参数不如用户自己调" —— 两个分母是不同人群
                                                （这一档全是走识别链路的加工，多为新材料首次试打）。
                                            </>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="text-xs text-slate-400 text-right leading-relaxed">
                                    这一档还没有已判定的加工
                                    {off && off.scanned > 0 ? `（共 ${off.scanned.toLocaleString()} 次，都还没打标）` : ''}。
                                    <br />
                                    全部加工归档的整体达标率是 <b>{heroRate != null ? `${heroRate}%` : '—'}</b>（已判定 {tierBase.toLocaleString()} 条），见下方「加工效果分布」。
                                </div>
                            )}
                        </div>
                    </div>
                    )}

                    {/* 各设备加工成功率 */}
                    {!studioOnly && (
                    <div className="card p-4">
                        <div className="flex items-baseline gap-2 mb-3">
                            <div className="text-sm font-semibold text-slate-700">各设备加工成功率</div>
                            {studioTag}
                            <div className="text-[11px] text-slate-400 ml-auto">达标 = 完美 + 轻微瑕疵 · n 是已判定条数</div>
                            <SegGroup
                                value={rateDesc ? 'desc' : 'asc'}
                                options={[{ value: 'asc', label: '低→高' }, { value: 'desc', label: '高→低' }]}
                                onChange={(v) => setRateDesc(v === 'desc')}
                            />
                        </div>
                        <SuccessRateBars rows={deviceRates} desc={rateDesc} colorMap={devColor} />
                        {(sum?.by_device || []).length > deviceRates.length && (
                            <div className="text-[11px] text-slate-400 mt-2">
                                另有 {(sum?.by_device || []).length - deviceRates.length} 台设备还没有已判定的加工（有加工但没打标或图像不可判读），算不出成功率，未列出。
                            </div>
                        )}
                        <div className="text-[11px] text-slate-400 mt-2 space-y-0.5">
                            {effect && effect.low_confidence > 0 && (
                                <div>另有 {effect.low_confidence} 条图像不可判读，已排除在分母外（与「加工效果分布」同一口径）。</div>
                            )}
                            <div>口径：全部加工归档。</div>
                        </div>
                    </div>
                    )}

                    {/* KPI 四张 */}
                    <div className="grid grid-cols-4 gap-4">
                        <StatCard label="加工总次数" value={(funnel?.process_events ?? 0).toLocaleString()} sub="加工事件数" />
                        <StatCard label="被加工材料" value={(funnel?.processed ?? 0).toLocaleString()}
                            sub={sentinelEvents > 0
                                ? `去重 · 仅含带材料标识的 ${eventsIdentified.toLocaleString()} 次加工`
                                : '去重'} />
                        {!studioOnly && (
                        <StatCard label="效果完美率"
                            value={effect && tierBase > 0 ? `${Math.round((tiers.perfect / tierBase) * 100)}%` : '—'}
                            sub={effect ? `${studioTag ? '仅 Studio 实时 · ' : ''}已判定 ${tierBase.toLocaleString()}${effect.low_confidence > 0 ? `（另有 ${effect.low_confidence} 条不可判读，未计入）` : ''}` : ''} />
                        )}
                        {!studioOnly && (
                        <StatCard label="参数采纳率"
                            value={adoption ? (adoptionOk ? `${adoption.adoption_rate}%` : '待回填') : '—'}
                            sub={adoption
                                ? adoption.unresolved === 0
                                    ? '推荐参数原样占比'
                                    : adoptionOk
                                        ? `基于 ${adoption.compared.toLocaleString()} 条 · 另有 ${adoption.unresolved} 条未回填`
                                        : `${adoption.unresolved} / ${adoption.scanned} 条的判定还没回填`
                                : ''} />
                        )}
                    </div>

                    {readLocal && <ProcLocalStats sum={sum} origin={origin} onDrill={drillLocal} />}

                    {/* 设备分布 | 材料分类 Top */}
                    <div className="grid grid-cols-2 gap-4">
                        <BarList title="设备分布" hint="加工事件按设备 · 点击下钻"
                            rows={(sum?.by_device || []).map((d) => ({
                                label: d.device || '未知', value: d.process_count,
                                onClick: d.device ? () => { setDevice(d.device); setPage(1); setTab('table'); } : undefined,
                            }))} />
                        <BarList title="材料分类 Top" hint="被加工材料按分类 · 取本平台材料主数据的一级类别 · 点击下钻" labelWidth="w-32" maxRows={8}
                            rows={byCategory.map((c) => ({
                                label: c.category, value: c.materials,
                                // 卡上数的是材料数 → 下钻到材料维度 (同「材料来源」卡)
                                onClick: categories.includes(c.category)
                                    ? () => { setCategory(c.category); setGrain('material'); setPage(1); setTab('table'); }
                                    : undefined,
                                share: funnel && funnel.processed > 0 ? `${Math.round((c.materials / funnel.processed) * 100)}%` : undefined,
                            }))}
                            footer={(
                                <div className="space-y-1">
                                    {sentinelNote}
                                    {unknownCat && (
                                        <div className="text-[11px] text-amber-600">
                                            另有 <b>{unknownCat.materials}</b> 个材料<b>在本平台主数据里查不到</b>（记作 Unknown，共加工 {unknownCat.process_count} 次）—— 都是用户自己在 Studio 里存的材料，没有耗材编码，所以没有分类。
                                        </div>
                                    )}
                                </div>
                            )} />
                    </div>

                    {/* 材料来源 (三分类, 与加工明细「来源」列同一判定) */}
                    <div className="grid grid-cols-2 gap-4">
                        <BarList title="材料来源" hint="被加工材料按来源 · 点条形筛明细" labelWidth="w-28"
                            footer={sentinelNote}
                            note="走过识别链路的优先算「材料识别」；其余按本平台材料主数据判官方/自定义。"
                            rows={(sum?.by_source || []).map((r) => ({
                                label: SOURCE_KIND_LABEL[r.source]?.text || r.source,
                                value: r.materials,
                                share: funnel && funnel.processed > 0 ? `${Math.round((r.materials / funnel.processed) * 100)}%` : undefined,
                                // 卡上数的是**材料数**, 所以下钻切到材料维度 —— 落到记录维度会看到
                                // 上报条数, 与卡上的数字不是一个单位.
                                onClick: () => { setSource(r.source); setGrain('material'); setPage(1); setTab('table'); },
                            }))} />
                    </div>

                    {/* 加工场景识别 —— 与「历史加工数据」页同一张卡 (SceneCard.tsx),
                        但数据来自 prism 的 scene_label_service 增量打标, 与那页的
                        存量打标是两条链路, 两边的数不该互相验算。 */}
                    <SceneCard scene={toSceneDist(sum?.scene)}
                        onTagClick={(t) => { setScene(t); setPage(1); setTab('table'); }}
                        onReasonClick={readLocal ? () => drillLocal({ scene_state: 'unevaluable' }) : undefined}
                        note="基于 prism 加工存档的结果图。一图可多标签，占比之和大于 100%。" />

                    {/* 加工效果分布 | 参数采纳 */}
                    {!studioOnly && (
                    <div className="grid grid-cols-2 gap-4">
                        <BarList title={studioTag ? '加工效果分布（仅 Studio 实时）' : '加工效果分布'} labelWidth="w-44" ordered
                            hint={effect ? `已判定 ${effect.labeled.toLocaleString()} · 待打标 ${effect.unlabeled}` : undefined}
                            rows={effect ? [
                                { label: TIER3.perfect.label, value: tiers.perfect, color: TIER3.perfect.bar, share: `${Math.round((tiers.perfect / Math.max(1, tierBase)) * 100)}%` },
                                { label: TIER3.minor.label, value: tiers.minor, color: TIER3.minor.bar, share: `${Math.round((tiers.minor / Math.max(1, tierBase)) * 100)}%` },
                                { label: TIER3.fail.label, value: tiers.fail, color: TIER3.fail.bar, share: `${Math.round((tiers.fail / Math.max(1, tierBase)) * 100)}%` },
                            ] : []}
                            footer={effect && (effect.low_confidence > 0 || tiers.unjudgable > 0 || topInd.length > 0) ? (
                                <div className="space-y-1 text-[11px] text-slate-400">
                                    {effect.low_confidence > 0 && <div>另有 {effect.low_confidence} 条图像不可判读（confidence &lt; 0.4），已排除在分布外。</div>}
                                    {tiers.unjudgable > 0 && <div>另有 {tiers.unjudgable} 条判为「无法判断」（高反光 / 不清晰 / 看不到材料），不计入分母。</div>}
                                    {topInd.length > 0 && (
                                        <div className="flex flex-wrap items-center gap-1 pt-0.5">
                                            <span>不达标指标 Top（点击筛明细）:</span>
                                            {topInd.slice(0, 8).map((t) => (
                                                <button key={t.code} type="button" className="px-1.5 py-0.5 rounded"
                                                    style={{ background: '#fef3c7', color: '#92400e' }}
                                                    onClick={() => { setIndicator(t.code); setPage(1); setTab('table'); }}>
                                                    {t.code} · {t.count}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : undefined} />
                        <BarList title={studioTag ? '参数采纳（仅 Studio 实时）' : '参数采纳'} labelWidth="w-36" ordered
                            hint={adoption && adoption.compared > 0 ? `采纳率 ${adoption.adoption_rate}% · 有推荐的材料里原样用推荐参数` : undefined}
                            rows={adoption ? [
                                { label: '推荐参数', value: adoption.adopted, color: '#6366f1', share: `${Math.round((adoption.adopted / Math.max(1, adoptTotal)) * 100)}%`, onClick: () => drillVerdict('adopted') },
                                { label: '调整了推荐参数', value: adoption.adjusted, color: '#a5b4fc', share: `${Math.round((adoption.adjusted / Math.max(1, adoptTotal)) * 100)}%`, onClick: () => drillVerdict('adjusted') },
                                { label: '无推荐可比', value: adoption.no_recommendation, color: '#fde68a', share: `${Math.round((adoption.no_recommendation / Math.max(1, adoptTotal)) * 100)}%`, onClick: () => drillVerdict('no_recommendation') },
                            ] : []}
                            footer={(
                                <div className="space-y-1">
                                    <div className="text-[11px] text-slate-400">
                                        采纳率的分母只有<b>有推荐可比</b>的那 {(adoption?.compared ?? 0).toLocaleString()} 条（推荐 + 调整）；
                                        「无推荐可比」是判定拿不到推荐参数的那批（多为直选官方材料的加工 —— 平台参数库里有推荐值，
                                        但还没接进采纳判定），<b>不等于用户自定义参数</b>。
                                    </div>
                                    {adoption && adoption.unresolved > 0 && (
                                        <div className="text-[11px] text-amber-600">⚠️ 有 {adoption.unresolved} 行的采纳判定还没回填，它们不落在任何一档里，所以采纳这几个数偏小。</div>
                                    )}
                                </div>
                            )} />
                    </div>
                    )}

                    {/* 加工最多的材料 Top */}
                    <BarList title="加工最多的材料 Top" hint="加工次数 · 标签为材料来源 · 点击下钻" labelWidth="w-56" maxRows={10}
                        footer={sentinelNote}
                        rows={(sum?.top_materials || []).map((m) => ({
                            label: m.material_name || m.material_id, value: m.process_count,
                            onClick: m.material_id ? () => {
                                setMaterialQ(m.material_id); setMaterialQDraft(m.material_id); setPage(1); setTab('table');
                            } : undefined,
                            tag: <SourceKindBadge kind={m.source_kind} />,
                        }))} />
                </div>
            ) : (
                <div className="space-y-4">
                    <NoticeBar>
                        这张表与「统计」tab <b>同口径</b>：<b>全部加工归档</b>，含官方材料、用户自定义与 AI 识别保存的
                        {funnel ? `（共 ${funnel.processed.toLocaleString()} 种材料 / ${funnel.process_events.toLocaleString()} 次加工）` : ''}。
                        下面的「口径」开关可缩到只看能归因到识别任务的那批，用来看识别→加工这条链路。
                    </NoticeBar>

                    {/* 明细筛选栏 (2026-10-21 改版): 常用一层始终可见, 其余收进「更多筛选」按主题分组;
                        生效的筛选在底部列成 chip, 可单个移除. */}
                    <div className="card px-4 py-3 space-y-3">
                        <SegRow>
                            <SegGroup label="维度" value={grain} onChange={(v) => { setGrain(v as Grain); setPage(1); }}
                                options={[{ value: 'record', label: '每次加工上报' }, { value: 'material', label: '按材料聚合' }]} />
                            <SegGroup label="来源" value={source} onChange={(v) => { setSource(v); setPage(1); }}
                                options={[{ value: '', label: '全部' }, { value: 'recognized', label: '材料识别' }, { value: 'official', label: '官方材料' }, { value: 'user_custom', label: '用户自定义' }]} />
                            <SegGroup label="口径" value={recognizedOnly} onChange={(v) => { setRecognizedOnly(v); setPage(1); }}
                                options={[{ value: '', label: '全部加工记录（与统计同口径）' }, { value: 'true', label: '只看识别链路的加工' }]} />
                            <HelpTip text="「来源」「分类」由本平台材料主数据关联得出（源材料 id ↔ 本平台旧库 ID），不是 prism 给的。「材料识别」= 走过识别链路的加工，优先于官方 / 自定义。" />
                        </SegRow>

                        {/* 搜索 + 常用下拉: 等宽栅格; 日期区间占两格, 起止与「~」不拆行 */}
                        <FilterGrid>
                            {/* 归档号 = 一次加工上报的唯一号 (详情页 URL 尾段 / 日志里带的那个),
                                精确匹配一条; 按材料聚合维度下就是该归档所属材料那一行。 */}
                            <input className="inp min-w-0" inputMode="numeric"
                                placeholder="归档号（精确）" value={archiveIdDraft}
                                title="加工归档号 (archive_id), 精确匹配一次加工上报"
                                onChange={(e) => setArchiveIdDraft(e.target.value.replace(/[^0-9]/g, ''))}
                                onBlur={() => { if (archiveIdDraft.trim() !== archiveId) { setArchiveId(archiveIdDraft.trim()); setPage(1); } }}
                                onKeyDown={(e) => { if (e.key === 'Enter') { setArchiveId(archiveIdDraft.trim()); setPage(1); } }} />
                            <input className="inp min-w-0"
                                placeholder="材料：名称 / 编码 / 旧库 ID" value={materialQDraft}
                                title="本平台材料按名称、xcode、旧库 ID 匹配; 用户自定义材料按源库原始 material_id 匹配"
                                onChange={(e) => setMaterialQDraft(e.target.value)}
                                onBlur={() => { if (materialQDraft.trim() !== materialQ) { setMaterialQ(materialQDraft.trim()); setPage(1); } }}
                                onKeyDown={(e) => { if (e.key === 'Enter') { setMaterialQ(materialQDraft.trim()); setPage(1); } }} />
                            <input className="inp min-w-0" placeholder="用户 UID / 邮箱" value={uidDraft}
                                onChange={(e) => setUidDraft(e.target.value)}
                                onBlur={() => { if (uidDraft.trim() !== uid) { setUid(uidDraft.trim()); setPage(1); } }}
                                onKeyDown={(e) => { if (e.key === 'Enter') { setUid(uidDraft.trim()); setPage(1); } }} />
                            <div className="min-w-0" style={{ gridColumn: 'span 2' }}>
                                <DateRangeInput from={dateFrom} to={dateTo} onChange={setDates} />
                            </div>
                            <SearchSelect className="w-full" value={device} onChange={(v) => { setDevice(v); setPage(1); }}
                                placeholder="全部设备" clearable
                                options={(sum?.by_device || []).filter((d) => d.device).map((d) => ({ value: d.device, label: d.device }))} />
                            {readLocal && <ProcLocalSection section="module" value={lf} onChange={setLocal} options={options} />}
                            <span title="本平台材料主数据的一级类别" className="min-w-0">
                                <SearchSelect className="w-full" value={category} onChange={(v) => { setCategory(v); setPage(1); }}
                                    placeholder="全部分类" clearable
                                    options={categories.map((c) => ({ value: c, label: c }))} />
                            </span>
                        </FilterGrid>

                        <SegRow>
                            {/* 「不达标」要送 acceptable,poor 两个值: 页面是三档归并口径
                                (tier3Of: best=完美 / good=轻微瑕疵 / acceptable+poor=不达标),
                                只送 poor 会漏掉 acceptable —— 线上 acceptable 有 10,490 条,
                                是四档里最大的一档, 徽标显示"不达标"却筛不出来。 */}
                            <SegGroup label="加工质量" value={quality} onChange={(v) => { setQuality(v); setPage(1); }}
                                options={[{ value: '', label: '全部' }, { value: 'best,perfect', label: '完美' }, { value: 'good,minor_flaw', label: '轻微瑕疵' }, { value: 'acceptable,poor,abnormal', label: '不达标' }, { value: 'unjudgable', label: '无法判断' }]} />
                            <SegGroup label="参数采纳" value={verdict} onChange={(v) => { setVerdict(v); setPage(1); }}
                                options={[{ value: '', label: '全部' }, { value: 'adopted', label: '推荐参数' }, { value: 'adjusted', label: '调整了推荐参数' }, { value: 'no_recommendation', label: '无推荐可比' }]} />
                            {/* 用户在 Studio 里的评价 (1.9 起); 多数加工没评价, 「未评价」与「不满意」分开筛 */}
                            <SegGroup label="是否满意" value={satisfied} onChange={(v) => { setSatisfied(v); setPage(1); }}
                                options={[{ value: '', label: '全部' }, { value: 'true', label: '满意' }, { value: 'false', label: '不满意' }, { value: 'none', label: '未评价' }]} />
                        </SegRow>

                        <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
                            <button type="button" className="text-xs font-semibold text-slate-600 hover:text-slate-900 flex items-center gap-1"
                                aria-expanded={moreOpen} onClick={() => setMoreOpen((x) => !x)}>
                                <span className="inline-block w-3 text-slate-400">{moreOpen ? '▾' : '▸'}</span>
                                更多筛选
                                {advancedCount > 0 && (
                                    <span className="ml-0.5 px-1.5 rounded-full bg-indigo-50 text-indigo-600 text-[10px] leading-4">{advancedCount}</span>
                                )}
                            </button>
                            <span className="text-[11px] text-slate-400">
                                效果打标 · 场景与图片{readLocal ? ' · 加工参数' : ''}{readLocal && origin !== 'studio' ? ' · 仅历史数据' : ''}
                            </span>
                            {readLocal && !dateFrom && !dateTo && listWindow?.default && (
                                <span className="ml-auto text-[11px] text-slate-400"
                                    title="明细数据量大, 未选日期时明细列表只看最近一段 (按所选数据来源的最新数据日期往前推); 统计卡片始终是全部数据; 选了日期则两者都按所选范围">
                                    未选日期：明细默认最近 {listWindow.days ?? 30} 天（{listWindow.date_from} ~ {listWindow.date_to}），统计为全部数据
                                </span>
                            )}
                        </div>

                        {moreOpen && (
                            <div className="space-y-3">
                                <FilterGroup title="效果打标">
                                    <SegRow>
                                        <SegGroup label="打标" value={labelSeg} onChange={onLabelSeg}
                                            title="已打标 / 待打标 看有没有效果标签; AI / 人工 看胜出标签是谁打的"
                                            options={[
                                                { value: '', label: '全部' }, { value: 'labeled', label: '已打标' }, { value: 'unlabeled', label: '待打标' },
                                                ...(readLocal ? [{ value: 'ai', label: 'AI' }, { value: 'manual', label: '人工' }] : []),
                                            ]} />
                                    </SegRow>
                                    {readLocal && <ProcLocalSection section="effect" value={lf} onChange={setLocal} options={options} />}
                                    <FilterGrid>
                                        <SearchSelect className="w-full" value={defect} onChange={(v) => { setDefect(v); setPage(1); }}
                                            placeholder="全部缺陷" clearable
                                            options={(effect?.top_defects || []).map((t) => ({ value: t.code, label: defectText(t.code) }))} />
                                        <span title="加工质量评估判出不达标的核心指标 (v2 口径)">
                                            <SearchSelect className="w-full" value={indicator} onChange={(v) => { setIndicator(v); setPage(1); }}
                                                placeholder="全部指标" clearable
                                                options={topInd.map((t) => ({ value: t.code, label: t.code }))} />
                                        </span>
                                    </FilterGrid>
                                </FilterGroup>
                                <FilterGroup title="场景与图片">
                                    {readLocal && <ProcLocalSection section="scene" value={lf} onChange={setLocal} options={options} />}
                                    <FilterGrid>
                                        {/* 场景选项取统计卡的八类 (prism 按加工存档打的标签, 词表收敛) */}
                                        <span title="加工场景 (prism 按加工存档打的胜出标签)" className="min-w-0">
                                            <SearchSelect className="w-full" value={scene} onChange={(v) => { setScene(v); setPage(1); }}
                                                placeholder="全部加工场景" clearable
                                                options={(sum?.scene?.tags || []).map((t) => ({ value: t.code, label: t.code }))} />
                                        </span>
                                        <span title="加工结果图有无; 线上约 7% 的上报没有结果图 (P3 机型不上报)" className="min-w-0">
                                            <SearchSelect className="w-full" value={hasResultImage}
                                                onChange={(v) => { setHasResultImage(v); setPage(1); }}
                                                placeholder="结果图不限" options={HAS_RESULT_IMAGE_OPTS} clearable />
                                        </span>
                                    </FilterGrid>
                                </FilterGroup>
                                {readLocal && (
                                    <FilterGroup title="加工参数">
                                        <ProcLocalSection section="params" value={lf} onChange={setLocal} options={options} />
                                    </FilterGroup>
                                )}
                                {readLocal && origin !== 'studio' && (
                                    <FilterGroup title="仅历史数据">
                                        <ProcLocalSection section="history" value={lf} onChange={setLocal} options={options} />
                                    </FilterGroup>
                                )}
                            </div>
                        )}

                        {chips.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-slate-100">
                                <span className="text-[11px] text-slate-400 mr-1">已筛</span>
                                {chips.map((c) => (
                                    <button key={c.key} type="button" title={`移除「${c.label}」筛选`}
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-[11px] text-slate-600 hover:bg-rose-50 hover:text-rose-600"
                                        onClick={c.clear}>
                                        {c.label}<span className="text-slate-400">×</span>
                                    </button>
                                ))}
                                <button type="button" className="ml-auto text-xs text-blue-600 hover:bg-blue-50 px-2 py-1 rounded"
                                    onClick={resetFilters}>清空</button>
                            </div>
                        )}
                    </div>

                    {error && <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">错误: {error}</div>}

                    <div className="card overflow-x-auto">
                        <div className="flex items-center px-4 pt-3 pb-2">
                            <div className="text-sm font-semibold text-slate-700">加工明细</div>
                            <div className="text-[11px] text-slate-400 ml-2">
                                {scopeText}
                                {readLocal && grain === 'record' && ` · 按加工时间${order === 'asc' ? '正序' : '倒序'}`}
                            </div>
                            <div className="ml-auto text-xs text-slate-400">
                                共 {total.toLocaleString()} {grain === 'record' ? '次加工上报' : '个材料'}
                            </div>
                        </div>
                        {sentinel > 0 && (
                            <div className="px-4 pb-2 text-[11px] text-amber-600">
                                ⚠️ 另有 {sentinel} 条加工存档没带材料标识（material_id 为空或 0），不能归到任何一个材料上，所以不在下表里。
                            </div>
                        )}
                        {grain === 'record' && (
                        <table className="w-full text-[13px]" style={{ minWidth: 900 }}>
                            <thead>
                                <tr className="text-left text-[10px] font-extrabold uppercase text-slate-400 border-b border-slate-100" style={{ letterSpacing: '1.5px', background: 'rgb(248 250 252 / 60%)' }}>
                                    {/* 列与材料维度同名同序, 三处不同:
                                        ① 多一列「用户」, 且「加工次数」改成**这个材料 × 这个用户**的次数
                                           (材料维度那列是跨所有用户的合计);
                                        ② 「最近加工」换成「加工时间」—— 一行就是一次上报, 时间要精确到
                                           分钟才分得出先后。
                                        ③ 多一列「是否满意」—— 用户对这一次的评价, 聚合到材料上没有意义。
                                        「参数采纳」「加工质量」在这里是**这一次上报自己的**判定, 不是材料的最近一次。 */}
                                    <th className="px-3 py-2.5 whitespace-nowrap">材料名称</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="XCS 画布底图 = 开工前相机拍的工作台/材料照; 源库约一成存档没有">加工前</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="加工结果图 (complete-img)">加工后</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="材料识别 = 走过识别链路; 官方材料 / 用户自定义 = 按本平台材料主数据判定">来源</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title={readLocal ? '设备模组 = 机型 × 激光模组 (光源 / 瓦数)' : undefined}>{readLocal ? '设备模组' : '设备'}</th>
                                    {readLocal && <th className="px-3 py-2.5 whitespace-nowrap">加工类型</th>}
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="本平台材料主数据的一级类别">分类</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="上报这次加工的用户; 点邮箱可只看这个用户">用户</th>
                                    <th className="px-3 py-2.5 pr-6 text-right whitespace-nowrap" title="这个材料被这个用户加工的次数 (当前筛选范围内); 匿名上报无法区分用户, 显示 —">加工次数</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="这一次上报的参数采纳判定">参数采纳</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="这一次上报的效果判定; 未打标显示「待打标」">加工质量</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="用户在 Studio 里对这次加工的评价 (1.9 起); 大多数加工用户没评, 显示「未评价」; 鼠标悬停看不满意原因">是否满意</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="这一次加工结果图的场景标签 (prism 按存档打, 一图可多标); 不可评估的标灰">场景</th>
                                    <th className="px-3 py-2.5 text-right whitespace-nowrap">
                                        {readLocal ? (
                                            <SortTimeHeader label="加工时间" order={order} onToggle={toggleOrder} />
                                        ) : '加工时间'}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={readLocal ? 14 : 13} className="text-center text-slate-400 py-10">加载中…</td></tr>
                                ) : records.length === 0 ? (
                                    <tr><td colSpan={readLocal ? 14 : 13} className="text-center text-slate-400 py-10">没有符合条件的加工记录</td></tr>
                                ) : (
                                    records.map((r) => {
                                        const q = r.effect?.quality || '';
                                        const failTier = tier3Of(q) === 'fail';
                                        return (
                                            <tr key={r.archive_id}
                                                className="border-b border-slate-50 cursor-pointer hover:bg-slate-50"
                                                style={failTier ? { boxShadow: 'inset 2px 0 0 #fda4af' } : undefined}
                                                onClick={() => history.push(`/assets/process-records/${r.archive_id}`)}>
                                                <td className="px-3 py-3">
                                                    <div className="font-medium text-slate-700 break-words max-w-[320px]" title={r.material_name}
                                                        style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                        {materialDisplayName(r.material_name, r.material_id)}
                                                    </div>
                                                    <div className="text-[11px] text-slate-400 font-mono truncate">
                                                        {isSentinelMaterialID(r.material_id) ? '无材料标识' : r.material_id}
                                                    </div>
                                                    {r.origin === 'history' && (
                                                        <span className="inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600">XCS 2.7 历史</span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-3">
                                                    <ProcessThumb src={r.pre_image_url} label="加工前" onZoom={setZoomSrc} />
                                                    {r.before_kind === 'restored' && (
                                                        <div className="mt-0.5 text-[10px] text-amber-600" title="模型从结果图反推的加工前样子, 不是实拍">模型还原</div>
                                                    )}
                                                </td>
                                                <td className="px-3 py-3">
                                                    <ProcessThumb src={r.image_url} label="加工后" onZoom={setZoomSrc} />
                                                </td>
                                                <td className="px-3 py-3"><SourceKindBadge kind={r.source_kind} /></td>
                                                <td className="px-3 py-3 font-mono text-[11px] text-slate-600 whitespace-nowrap">{(readLocal && r.device_module) || r.device_model || '—'}</td>
                                                {readLocal && (
                                                    <td className="px-3 py-3 text-[11px] text-slate-600 whitespace-nowrap">{ptText(r.processing_type)}</td>
                                                )}
                                                <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{r.category || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                                                <td className="px-3 py-3">
                                                    {/* 「只看这个用户」下推的是 uid 筛选 (后端按 user_id 匹配),
                                                        所以只有拿得到 user_id 才做成按钮; 只有邮箱的行纯展示。 */}
                                                    {r.user_id ? (
                                                        <button type="button"
                                                            className="text-left text-[11px] text-blue-600 hover:underline max-w-[180px] truncate block"
                                                            title={`只看用户 ${r.user_id}`}
                                                            onClick={(e) => { e.stopPropagation(); setUid(r.user_id); setUidDraft(r.user_id); setPage(1); }}>
                                                            {r.user_email || r.user_id}
                                                        </button>
                                                    ) : r.user_email ? (
                                                        <div className="text-[11px] text-slate-600 truncate max-w-[180px]">{r.user_email}</div>
                                                    ) : <span style={{ color: '#cbd5e1' }}>匿名</span>}
                                                    {r.user_email && r.user_id && (
                                                        <div className="text-[11px] text-slate-400 font-mono truncate max-w-[180px]">{r.user_id}</div>
                                                    )}
                                                </td>
                                                <td className="px-3 py-3 pr-6 text-right font-mono tabular-nums text-slate-600">
                                                    {r.user_process_count > 0
                                                        ? r.user_process_count
                                                        : <span style={{ color: '#cbd5e1' }}>—</span>}
                                                </td>
                                                {/* 历史数据没有参数采纳与效果打标 (上报里就没有), 显示「—」而不是「待打标」 */}
                                                <td className="px-3 py-3">{r.origin === 'history' ? <span style={{ color: '#cbd5e1' }}>—</span> : <AdoptBadge verdict={r.verdict} wording="v44" />}</td>
                                                <td className="px-3 py-3">{r.origin === 'history' ? <span style={{ color: '#cbd5e1' }}>—</span> : <EffectBadge quality={q} wording="three" />}</td>
                                                <td className="px-3 py-3"><SatisfactionBadge value={r.satisfaction} /></td>
                                                <td className="px-3 py-3">
                                                    {!r.scene ? <span style={{ color: '#cbd5e1' }}>—</span>
                                                        : !r.scene.evaluable ? (
                                                            <span className="text-[11px] text-slate-400" title={r.scene.unable_reason}>不可评估</span>
                                                        ) : (
                                                            <div className="flex flex-wrap gap-1 max-w-[150px]">
                                                                {r.scene.tags.map((t) => (
                                                                    <span key={t} className="px-1.5 py-0.5 rounded text-[11px] whitespace-nowrap"
                                                                        style={{ background: '#f1f5f9', color: '#475569' }}>{t}</span>
                                                                ))}
                                                            </div>
                                                        )}
                                                </td>
                                                <td className="px-3 py-3 text-right text-[11px] font-mono text-slate-500 whitespace-nowrap">
                                                    {fmtMinute(r.archived_at)}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                        )}
                        {grain === 'material' && (
                        <table className="w-full text-[13px]" style={{ minWidth: 860 }}>
                            <thead>
                                <tr className="text-left text-[10px] font-extrabold uppercase text-slate-400 border-b border-slate-100" style={{ letterSpacing: '1.5px', background: 'rgb(248 250 252 / 60%)' }}>
                                    {/* 不写列宽, 走浏览器 table-auto 按内容比例分配 —— 与平台其它表格
                                        (SyncLogList / DictAdmin 等) 同一写法; 富余空间自然摊到各列,
                                        不会像固定布局那样在某一列里留出空洞。长文本靠单元格内层
                                        max-w + clamp 收口。
                                        列: 材料名称 · 加工前 · 加工后 · 来源 · 设备 · 分类 · 加工次数 ·
                                        参数采纳 · 加工质量 · 场景 · 最近加工 (原型/原平台把图摆在最前,
                                        这里材料名称提到第一列 —— 一行先认材料, 图跟在后面看效果)。
                                        来源/分类由本平台材料主数据关联得出 (原平台这两列灰着"待后端");
                                        场景依赖 LLM 打标, 仍为灰列。数值列右对齐且右侧留 pr-6。 */}
                                    <th className="px-3 py-2.5 whitespace-nowrap">材料名称</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="XCS 画布底图 = 开工前相机拍的工作台/材料照; 源库约一成存档没有。取最近一次加工">加工前</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="加工结果图 (complete-img), 取最近一次加工">加工后</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="材料识别 = 走过识别链路; 官方材料 / 用户自定义 = 按本平台材料主数据判定">来源</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap">设备</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="本平台材料主数据的一级类别">分类</th>
                                    <th className="px-3 py-2.5 pr-6 text-right whitespace-nowrap" title="这份材料被加工的次数">加工次数</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap">参数采纳</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap">加工质量</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap text-slate-300" title="场景是按每次加工的结果图打的, 材料聚合行不给 —— 切到「每次加工」维度看">场景</th>
                                    <th className="px-3 py-2.5 text-right whitespace-nowrap">最近加工</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={11} className="text-center text-slate-400 py-10">加载中…</td></tr>
                                ) : list.length === 0 ? (
                                    <tr><td colSpan={11} className="text-center text-slate-400 py-10">没有符合条件的加工记录</td></tr>
                                ) : (
                                    list.map((r) => {
                                        const failTier = tier3Of(r.latest_effect || '') === 'fail';
                                        return (
                                            <tr key={r.material_id}
                                                className="border-b border-slate-50 cursor-pointer hover:bg-slate-50"
                                                style={failTier ? { boxShadow: 'inset 2px 0 0 #fda4af' } : undefined}
                                                onClick={() => history.push(`/assets/process-records/${r.latest_archive_id}`)}>
                                                <td className="px-3 py-3">
                                                    <div className="font-medium text-slate-700 break-words max-w-[320px]" title={r.material_name}
                                                        style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                        {materialDisplayName(r.material_name, r.material_id)}
                                                    </div>
                                                    <div className="text-[11px] text-slate-400 font-mono truncate">{r.material_id}</div>
                                                </td>
                                                <td className="px-3 py-3">
                                                    <ProcessThumb src={r.pre_image_url} label="加工前" onZoom={setZoomSrc} />
                                                </td>
                                                <td className="px-3 py-3">
                                                    <ProcessThumb src={r.image_url} label="加工后" onZoom={setZoomSrc} />
                                                </td>
                                                <td className="px-3 py-3"><SourceKindBadge kind={r.source_kind} /></td>
                                                <td className="px-3 py-3 font-mono text-[11px] text-slate-600 whitespace-nowrap">{r.device_model || '—'}</td>
                                                <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{r.category || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                                                <td className="px-3 py-3 pr-6 text-right font-mono tabular-nums text-slate-600">{r.process_count}</td>
                                                <td className="px-3 py-3"><AdoptBadge verdict={r.latest_verdict} wording="v44" /></td>
                                                <td className="px-3 py-3">
                                                    <EffectBadge quality={r.latest_effect || ''} wording="three" />
                                                </td>
                                                <td className="px-3 py-3" style={{ color: '#cbd5e1' }}>—</td>
                                                <td className="px-3 py-3 text-right text-[11px] font-mono text-slate-500 whitespace-nowrap">
                                                    {fmtDay(r.latest_archived_at)}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                        )}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-slate-100 text-xs text-slate-500">
                                <div>
                                    共 {total.toLocaleString()}{grain === 'record' ? ' 次加工上报' : ' 个材料'}（每页 {PAGE_SIZE}）
                                </div>
                                <Pagination page={page} totalPages={totalPages} onChange={setPage} />
                            </div>
                        )}
                        <div className="px-4 py-2.5 border-t border-slate-50 text-[11px] text-slate-400">
                            {grain === 'record' ? (
                                <>
                                    一行一次<b>加工上报</b>，「参数采纳」「加工质量」就是这一次自己的判定；点开看这一次的推荐 → 实际参数对照。
                                    「加工次数」是<b>这个材料被这个用户</b>加工的次数（当前筛选范围内）——
                                    要看跨所有用户的合计请切到「按材料聚合」。匿名上报（源库没带用户标识）彼此分不出是不是同一个人，次数显示「—」。
                                    效果要人工/AI 打标后才有，未打标的显示「待打标」。「是否满意」是用户自己在 Studio 里的评价（1.9 起才有，多数加工用户没评，显示「未评价」）。
                                    没带材料标识的上报在这里也会列出来。
                                </>
                            ) : (
                                <>
                                    一行一个<b>材料</b>，「加工次数」是它被<b>所有用户</b>加工过几次（不按用户拆）；点开看每一次的推荐 → 实际参数对照。
                                    「参数采纳」「加工质量」取<b>最近那一次</b>的判定 —— 要逐次看请切到「每次加工上报」。
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <ImageLightbox src={viewUrl(zoomSrc)} onClose={() => setZoomSrc(null)} />
        </div>
    );
}

export default ProcessList;
